import React, { useContext, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  WizardFormField,
  WizardStep,
} from "../../machines/immersive-wizard.machine";
import { WebsiteContentProps } from "../../types/wizard.types";
import WeekCalendar from "./WeekCalendar";
import {
  planDisplayMapping,
  appointmentPackageDisplayMapping,
} from "../../utils/membershipData";
import {
  getFormattedPrice,
  getSupportedCurrency,
} from "../../utils/currencyUtils";
import { AppContext } from "../../../../context/app";
import useTailwindBreakpoint from "../../../../hooks/useTailwindBreakpoint";

const WebsiteContent: React.FC<WebsiteContentProps> = ({
  appointmentsEnabled,
  classesEnabled,
  brandColor,
  currentStep,
  formData,
}) => {
  const { isXlScreen } = useTailwindBreakpoint();
  const selectedPlans = formData?.[WizardFormField.MembershipPlans] || [];
  const selectedAppointmentPackages =
    formData?.[WizardFormField.SelectedAppointmentPackages] || [];

  const app = useContext(AppContext);
  const { client } = app;
  const currency = getSupportedCurrency(client?.currencyIso);

  // Refs for scrolling to sections
  const calendarRef = useRef<HTMLDivElement>(null);
  const plansRef = useRef<HTMLDivElement>(null);
  const appointmentPackagesRef = useRef<HTMLDivElement>(null);

  const topOffset = 20;

  // Auto-scroll to sections based on current step and selections
  useEffect(() => {
    const scrollToElement = (element: HTMLElement) => {
      // Find the scrollable parent container
      const scrollableParent = element.closest(".tw-overflow-y-auto");
      if (scrollableParent) {
        const elementRect = element.getBoundingClientRect();
        const parentRect = scrollableParent.getBoundingClientRect();
        const scrollTop = scrollableParent.scrollTop;
        const targetScrollTop =
          scrollTop + elementRect.top - parentRect.top - topOffset;

        scrollableParent.scrollTo({
          top: targetScrollTop,
          behavior: "smooth",
        });
      }
    };

    const shouldScrollToCalendar =
      currentStep === WizardStep.AppointmentTypes && calendarRef.current;
    if (shouldScrollToCalendar) {
      setTimeout(() => {
        scrollToElement(calendarRef.current!);
      }, 300);
    }

    const shouldScrollToPlans = selectedPlans.length > 0 && plansRef.current;
    if (shouldScrollToPlans) {
      setTimeout(() => {
        scrollToElement(plansRef.current!);
      }, 300);
    }

    const shouldScrollToAppointmentPackages =
      selectedAppointmentPackages.length > 0 && appointmentPackagesRef.current;
    if (shouldScrollToAppointmentPackages) {
      setTimeout(() => {
        scrollToElement(appointmentPackagesRef.current!);
      }, 300);
    }
  }, [currentStep, selectedPlans.length, selectedAppointmentPackages.length]);

  const displayPlans = selectedPlans
    .map((planType) => {
      const planData =
        planDisplayMapping[planType as keyof typeof planDisplayMapping];
      return planData
        ? {
            id: planType,
            name: planData.name,
            price: getFormattedPrice(planData.pricing, currency),
            description: planData.description,
          }
        : null;
    })
    .filter(Boolean);

  const displayAppointmentPackages = selectedAppointmentPackages
    .map((packageType) => {
      const packageData =
        appointmentPackageDisplayMapping[
          packageType as keyof typeof appointmentPackageDisplayMapping
        ];
      return packageData
        ? {
            id: packageType,
            name: packageData.name,
            price: getFormattedPrice(packageData.pricing, currency),
            description: packageData.description,
          }
        : null;
    })
    .filter(Boolean);

  return (
    <div className="tw-min-h-full tw-bg-white tw-flex">
      {/* Left Navigation Sidebar - Only show on large screens */}
      {isXlScreen && (
        <div className="tw-w-1/5 tw-bg-gray-100 tw-border-r tw-border-gray-200 tw-min-h-full">
          <div className="tw-p-4 tw-space-y-3">
            {/* Navigation Items Skeleton */}
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div
                key={i}
                className="tw-flex tw-items-center tw-space-x-3 tw-px-3 tw-py-2"
              >
                <div className="tw-h-4 tw-bg-gray-300 tw-rounded tw-flex-1 tw-animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Main Content Area */}
      <div className="tw-flex-1 tw-bg-white">
        <div className="tw-p-8 tw-space-y-6">
          {/* Calendar Section - Show when appointments/classes are enabled */}
          <AnimatePresence>
            {(appointmentsEnabled || classesEnabled) && (
              <div ref={calendarRef}>
                <WeekCalendar
                  appointmentsEnabled={appointmentsEnabled}
                  selectedAppointmentTypes={
                    formData?.[WizardFormField.SelectedAppointmentTypes] || []
                  }
                  classesEnabled={classesEnabled}
                  brandColor={brandColor}
                  selectedClassTypes={
                    formData?.[WizardFormField.SelectedClassTypes] || []
                  }
                  extractedScheduleData={
                    formData?.[WizardFormField.ExtractedScheduleData]
                  }
                  extractedAppointmentScheduleData={
                    formData?.[WizardFormField.ExtractedAppointmentScheduleData]
                  }
                  currentStep={currentStep}
                />
              </div>
            )}
          </AnimatePresence>

          {/* Content Cards - Show when no scheduling enabled or as additional content */}
          <AnimatePresence>
            {!appointmentsEnabled &&
            !classesEnabled &&
            selectedPlans.length === 0 &&
            selectedAppointmentPackages.length === 0 ? (
              // Skeleton content when no scheduling and no plans selected
              <motion.div
                className="tw-space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="tw-grid tw-grid-cols-1 tw-md:grid-cols-2 tw-lg:grid-cols-3 tw-gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <motion.div
                      key={i}
                      className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.1 }}
                    >
                      <div className="tw-space-y-3">
                        <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-1/2 tw-animate-pulse"></div>
                        <div className="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-animate-pulse tw-mt-4"></div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className="tw-space-y-4">
                    <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-48 tw-animate-pulse"></div>
                    <div className="tw-space-y-2">
                      <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-full tw-animate-pulse"></div>
                      <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-full tw-animate-pulse"></div>
                      <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-4/5 tw-animate-pulse"></div>
                    </div>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-mt-6">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="tw-h-24 tw-bg-gray-200 tw-rounded tw-animate-pulse"
                        ></div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          {/* Membership Plans Section - Show when plans are selected */}
          <AnimatePresence>
            {selectedPlans.length > 0 && (
              <motion.div
                className="tw-space-y-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                ref={plansRef}
              >
                <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">
                  Membership Plans
                </h3>
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  {displayPlans.map((plan, index) => (
                    <motion.div
                      key={plan.id}
                      className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <h4 className="tw-font-semibold tw-text-gray-900 tw-mb-2">
                        {plan.name}
                      </h4>
                      <p className="tw-text-gray-600">
                        {plan.price} • {plan.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Appointment Packages Section - Show when appointment packages are selected */}
          <AnimatePresence>
            {selectedAppointmentPackages.length > 0 && (
              <motion.div
                className="tw-space-y-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                ref={appointmentPackagesRef}
              >
                <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">
                  Appointment Packages
                </h3>
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  {displayAppointmentPackages.map(
                    (appointmentPackage, index) => (
                      <motion.div
                        key={appointmentPackage.id}
                        className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <h4 className="tw-font-semibold tw-text-gray-900 tw-mb-2">
                          {appointmentPackage.name}
                        </h4>
                        <p className="tw-text-gray-600">
                          {appointmentPackage.price} •{" "}
                          {appointmentPackage.description}
                        </p>
                      </motion.div>
                    ),
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default WebsiteContent;
